import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';
import LayoutWrapper from '../components/Layout/LayoutWrapper';
import { LoadingSpinner, ErrorHandler } from '../components/common/ErrorHandler';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Coins as CoinsIcon, Plus, Edit, Trash2, Eye, Settings, TrendingUp, TrendingDown, RefreshCw, Download, FileText, BarChart3 } from 'lucide-react';
import useToast from '../hooks/useToast';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B9D', '#C0C0C0', '#FFD700'];

const Coins = () => {
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState('entry');
  const [dashboard, setDashboard] = useState(null);
  const [entries, setEntries] = useState([]);
  const [recentEntries, setRecentEntries] = useState([]);
  const [denominations, setDenominations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [entryType, setEntryType] = useState('Opening');
  const [counts, setCounts] = useState({});
  const [saving, setSaving] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Modals
  const [historyModal, setHistoryModal] = useState({ open: false, data: null });
  const [denominationsModal, setDenominationsModal] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, entry: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, entryId: null });

  // Filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // New denomination
  const [newDenomValue, setNewDenomValue] = useState('');

  // Reports
  const [reportStats, setReportStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [reportFromDate, setReportFromDate] = useState('');
  const [reportToDate, setReportToDate] = useState('');

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchDenominations();
    fetchDashboard();
    fetchHistory();
    fetchRecentEntries();
    fetchReportData();
  }, [date]);

  const fetchDenominations = async () => {
    try {
      const response = await api.get('/api/coins/denominations');
      setDenominations(response.data);
      // Initialize counts
      const initial = {};
      response.data.forEach(d => { initial[d.value] = 0; });
      setCounts(initial);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/coins/dashboard', { params: { date } });
      setDashboard(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const response = await api.get('/api/coins/history', { params });
      setEntries(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchRecentEntries = async () => {
    try {
      const response = await api.get('/api/coins/recent');
      setRecentEntries(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchReportData = async () => {
    try {
      const response = await api.get('/api/coins/reports');
      setReportStats(response.data.stats);
      setChartData(response.data.chart_data);
    } catch (err) {
      console.error(err);
    }
  };

  const calculateTotal = () => {
    return Object.entries(counts).reduce((sum, [denom, count]) => sum + (parseInt(denom) * parseInt(count || 0)), 0);
  };

  const pieChartData = useMemo(() => {
    return Object.entries(counts)
      .filter(([denom, count]) => count > 0)
      .map(([denom, count]) => ({
        name: `${denom}`,
        value: parseInt(denom) * parseInt(count),
      }));
  }, [counts]);

  const handleSaveEntry = async () => {
    try {
      setSaving(true);
      await api.post('/api/coins/entries', {
        date,
        entry_type: entryType,
        denominations: counts
      });
      showSuccess('Entry saved successfully');
      fetchDashboard();
      fetchHistory();
      fetchRecentEntries();
      fetchReportData();
      // Reset counts
      const reset = {};
      denominations.forEach(d => { reset[d.value] = 0; });
      setCounts(reset);
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleEditEntry = async (entryId) => {
    try {
      const response = await api.get(`/api/coins/entries/${entryId}`);
      setEditModal({ open: true, entry: response.data });
      setCounts(response.data.denominations);
      setDate(response.data.date);
      setEntryType(response.data.entry_type);
      setActiveTab('entry');
    } catch (err) {
      showError('Failed to load entry');
    }
  };

  const handleDeleteEntry = async () => {
    try {
      await api.delete(`/api/coins/entries/${deleteModal.entryId}`);
      showSuccess('Entry deleted successfully');
      setDeleteModal({ open: false, entryId: null });
      fetchDashboard();
      fetchHistory();
      fetchRecentEntries();
      fetchReportData();
    } catch (err) {
      showError('Failed to delete entry');
    }
  };

  const handleViewDetails = (entry) => {
    setHistoryModal({ open: true, data: entry });
  };

  const handleRefresh = () => {
    fetchDashboard();
    fetchHistory();
    fetchRecentEntries();
    fetchReportData();
    showSuccess('Data refreshed');
  };

  const handleAddDenomination = async () => {
    try {
      const value = parseInt(newDenomValue);
      if (!value || value <= 0) {
        showError('Invalid denomination value');
        return;
      }
      await api.post('/api/coins/denominations', { value });
      showSuccess('Denomination added successfully');
      setNewDenomValue('');
      fetchDenominations();
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to add denomination');
    }
  };

  const handleDeleteDenomination = async (value) => {
    try {
      await api.delete(`/api/coins/denominations/${value}`);
      showSuccess('Denomination deleted successfully');
      fetchDenominations();
    } catch (err) {
      showError('Failed to delete denomination');
    }
  };

  const handleExportExcel = async () => {
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const response = await api.get('/api/coins/export/excel', {
        params,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `coins_history_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSuccess('Excel file downloaded');
    } catch (err) {
      showError('Failed to export Excel');
    }
  };

  const handleExportPDF = async () => {
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const response = await api.get('/api/coins/export/pdf', {
        params,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `coins_history_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSuccess('PDF file downloaded');
    } catch (err) {
      showError('Failed to export PDF');
    }
  };

  const handleGenerateReport = async () => {
    try {
      const params = {};
      if (reportFromDate) params.from_date = reportFromDate;
      if (reportToDate) params.to_date = reportToDate;
      const response = await api.get('/api/coins/reports/pdf', {
        params,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `coins_report_${reportFromDate}_to_${reportToDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSuccess('Report generated successfully');
    } catch (err) {
      showError('Failed to generate report');
    }
  };

  const filteredEntries = entries.filter(entry => {
    if (searchQuery) {
      return entry.date.includes(searchQuery) ||
             entry.opening_total?.toString().includes(searchQuery) ||
             entry.closing_total?.toString().includes(searchQuery);
    }
    return true;
  });

  if (loading) {
    return <LayoutWrapper title="Coins Management"><LoadingSpinner /></LayoutWrapper>;
  }

  return (
    <LayoutWrapper
      title="Coins Management"
      subtitle="Track and manage your coin entries"
      actions={
        <div className="flex gap-2 items-center">
          <Button variant="outline" size="sm" onClick={() => setDenominationsModal(true)}>
            <Settings className="w-4 h-4 mr-1" /> Denominations
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <div className="text-sm text-gray-600 ml-2">
            {currentTime.toLocaleString('en-IN', {
              dateStyle: 'medium',
              timeStyle: 'medium'
            })}
          </div>
        </div>
      }
    >
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-500">Today's Opening</p>
                <p className="text-2xl font-bold text-green-600">{(dashboard?.opening_total || 0).toLocaleString('en-IN')}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-500">Today's Closing</p>
                <p className="text-2xl font-bold text-blue-600">{(dashboard?.closing_total || 0).toLocaleString('en-IN')}</p>
              </div>
              <TrendingDown className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-500">Difference</p>
                <p className={`text-2xl font-bold ${dashboard?.difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {dashboard?.difference >= 0 ? '+' : ''}{Math.abs(dashboard?.difference || 0).toLocaleString('en-IN')}
                </p>
              </div>
              {dashboard?.difference >= 0 ? (
                <TrendingUp className="w-8 h-8 text-green-500 opacity-50" />
              ) : (
                <TrendingDown className="w-8 h-8 text-red-500 opacity-50" />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4 bg-gray-100 rounded-lg p-1">
  <TabsTrigger
    value="entry"
    className="
      text-sm
      transition-colors
      data-[state=active]:bg-blue-700
      data-[state=active]:text-white
      data-[state=active]:font-semibold
      data-[state=active]:shadow-sm
    "
  >
    New Entry
  </TabsTrigger>

  <TabsTrigger
    value="history"
    className="
      text-sm
      transition-colors
      data-[state=active]:bg-blue-700
      data-[state=active]:text-white
      data-[state=active]:font-semibold
      data-[state=active]:shadow-sm
    "
  >
    History
  </TabsTrigger>

  <TabsTrigger
    value="reports"
    className="
      text-sm
      transition-colors
      data-[state=active]:bg-blue-700
      data-[state=active]:text-white
      data-[state=active]:font-semibold
      data-[state=active]:shadow-sm
    "
  >
    Reports
  </TabsTrigger>
</TabsList>


        {/* NEW ENTRY TAB */}
        <TabsContent value="entry">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Coin Entry</CardTitle>
                    <div className="flex gap-4 items-center">
                      <div>
                        <Label className="text-xs">Date</Label>
                        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" />
                      </div>
                      <div>
                        <Label className="text-xs">Entry Type</Label>
                        <Select value={entryType} onValueChange={setEntryType}>
                          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Opening">Opening</SelectItem>
                            <SelectItem value="Closing">Closing</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow style={{ backgroundColor: '#1e3a8a' }}>
                          <TableHead className="font-semibold" style={{ color: 'white' }}>DENOMINATION</TableHead>
                          <TableHead className="font-semibold" style={{ color: 'white' }}>COUNT</TableHead>
                          <TableHead className="font-semibold" style={{ color: 'white' }}>TOTAL</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {denominations.sort((a, b) => a.value - b.value).map(d => (
                          <TableRow key={d.value}>
                            <TableCell className="font-medium">{d.value}</TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="0"
                                value={counts[d.value] || 0}
                                onChange={(e) => setCounts({ ...counts, [d.value]: parseInt(e.target.value) || 0 })}
                                className="w-24"
                                data-testid={`denomination-input-${d.value}`}
                              />
                            </TableCell>
                            <TableCell className="font-medium">{((counts[d.value] || 0) * d.value).toLocaleString('en-IN')}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-gray-100 font-bold border-t-2">
                          <TableCell colSpan={2} className="text-right pr-4">GRAND TOTAL</TableCell>
                          <TableCell className="text-lg">{calculateTotal().toLocaleString('en-IN')}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex justify-end gap-4 mt-6">
                    <Button variant="outline" onClick={() => {
                      const reset = {};
                      denominations.forEach(d => { reset[d.value] = 0; });
                      setCounts(reset);
                    }} data-testid="reset-button">
                      Reset
                    </Button>
                    <Button onClick={handleSaveEntry} disabled={saving} data-testid="save-entry-button">
                      {saving ? 'Saving...' : 'Save Entry'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Entries */}
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="text-lg">Recent Entries (Last 10)</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>DATE</TableHead>
                        <TableHead>TYPE</TableHead>
                        <TableHead>TOTAL</TableHead>
                        <TableHead>ACTION</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recentEntries.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                            No recent entries found
                          </TableCell>
                        </TableRow>
                      ) : (
                        recentEntries.map((entry) => (
                          <TableRow key={entry.id}>
                            <TableCell className="font-medium">{entry.date}</TableCell>
                            <TableCell>
                              <Badge variant={entry.entry_type === 'Opening' ? 'default' : 'secondary'}>
                                {entry.entry_type}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium">{entry.total?.toLocaleString('en-IN')}</TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => handleEditEntry(entry.id)} data-testid={`edit-entry-${entry.id}`}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setDeleteModal({ open: true, entryId: entry.id })} data-testid={`delete-entry-${entry.id}`}>
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>

            {/* Live Pie Chart */}
            <div>
              <Card className="sticky top-4">
                <CardHeader>
                  <CardTitle className="text-lg">Current Entry Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  {pieChartData.length === 0 ? (
                    <div className="h-64 flex items-center justify-center text-gray-400">
                      <div className="text-center">
                        <CoinsIcon className="w-16 h-16 mx-auto mb-2 opacity-50" />
                        <p>Enter counts to see distribution</p>
                      </div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={pieChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {pieChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => `${value.toLocaleString('en-IN')}`} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-gray-600">Current Total</p>
                    <p className="text-2xl font-bold text-blue-600">{calculateTotal().toLocaleString('en-IN')}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* HISTORY TAB */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <CardTitle>Entry History</CardTitle>
                <div className="flex flex-wrap gap-2">
                  <Input
                    type="date"
                    placeholder="From Date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="w-40"
                  />
                  <Input
                    type="date"
                    placeholder="To Date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="w-40"
                  />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-40"
                  />
                  <Button variant="outline" size="sm" onClick={fetchHistory}>
                    Apply Filters
                  </Button>
                </div>
              </div>
              <div className="flex gap-2 mt-2">
                <Button variant="outline" size="sm" onClick={handleExportExcel} data-testid="export-excel-button">
                  <Download className="w-4 h-4 mr-1" /> Export Excel
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="export-pdf-button">
                  <FileText className="w-4 h-4 mr-1" /> Export PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>DATE</TableHead>
                    <TableHead>OPENING</TableHead>
                    <TableHead>CLOSING</TableHead>
                    <TableHead>DIFFERENCE</TableHead>
                    <TableHead>DETAILS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEntries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                        No entries found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredEntries.map(entry => (
                      <TableRow key={entry.date}>
                        <TableCell className="font-medium">{entry.date}</TableCell>
                        <TableCell className="font-medium">{(entry.opening_total || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell className="font-medium">{(entry.closing_total || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell>
                          <Badge variant={entry.difference >= 0 ? 'default' : 'destructive'} className={entry.difference >= 0 ? 'bg-green-500' : 'bg-red-500'}>
                            {entry.difference >= 0 ? '+' : ''}{Math.abs(entry.difference || 0).toLocaleString('en-IN')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline" onClick={() => handleViewDetails(entry)} data-testid={`view-details-${entry.date}`}>
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* REPORTS TAB */}
        <TabsContent value="reports">
          <div className="space-y-6">
            {/* Summary Statistics */}
            <Card>
              <CardHeader>
                <CardTitle>Summary Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-gray-600">Total Opening</p>
                    <p className="text-2xl font-bold text-green-600">{(reportStats?.total_opening || 0).toLocaleString('en-IN')}</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-gray-600">Total Closing</p>
                    <p className="text-2xl font-bold text-blue-600">{(reportStats?.total_closing || 0).toLocaleString('en-IN')}</p>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-lg">
                    <p className="text-sm text-gray-600">Net Difference</p>
                    <p className={`text-2xl font-bold ${reportStats?.net_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {reportStats?.net_difference >= 0 ? '+' : ''}{Math.abs(reportStats?.net_difference || 0).toLocaleString('en-IN')}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-sm text-gray-600">Days Tracked</p>
                    <p className="text-2xl font-bold text-purple-600">{reportStats?.days_tracked || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Daily Opening vs Closing (Last 10 Days)</CardTitle>
              </CardHeader>
              <CardContent>
                {chartData.length === 0 ? (
                  <div className="h-64 flex items-center justify-center text-gray-400">
                    <div className="text-center">
                      <BarChart3 className="w-16 h-16 mx-auto mb-2 opacity-50" />
                      <p>No data available</p>
                    </div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip formatter={(value) => `${value.toLocaleString('en-IN')}`} />
                      <Legend />
                      <Bar dataKey="opening" fill="#10b981" name="Opening" />
                      <Bar dataKey="closing" fill="#3b82f6" name="Closing" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* PDF Report Generation */}
            <Card>
              <CardHeader>
                <CardTitle>PDF Report Generation</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-4 items-end">
                  <div className="flex-1">
                    <Label>From Date</Label>
                    <Input
                      type="date"
                      value={reportFromDate}
                      onChange={(e) => setReportFromDate(e.target.value)}
                    />
                  </div>
                  <div className="flex-1">
                    <Label>To Date</Label>
                    <Input
                      type="date"
                      value={reportToDate}
                      onChange={(e) => setReportToDate(e.target.value)}
                    />
                  </div>
                  <Button
                    onClick={handleGenerateReport}
                    disabled={!reportFromDate || !reportToDate}
                    data-testid="generate-pdf-report-button"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Generate PDF Report
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Details Modal */}
      <Dialog open={historyModal.open} onOpenChange={(open) => !open && setHistoryModal({ open: false, data: null })}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Coin Entry Details - {historyModal.data?.date}</DialogTitle>
          </DialogHeader>
          {historyModal.data && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-sm text-gray-500">Opening Total</p>
                    <p className="text-xl font-bold text-green-600">{historyModal.data.opening_total?.toLocaleString('en-IN')}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-sm text-gray-500">Closing Total</p>
                    <p className="text-xl font-bold text-blue-600">{historyModal.data.closing_total?.toLocaleString('en-IN')}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-sm text-gray-500">Difference</p>
                    <p className={`text-xl font-bold ${historyModal.data.difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {historyModal.data.difference >= 0 ? '+' : ''}{Math.abs(historyModal.data.difference || 0).toLocaleString('en-IN')}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Opening Denominations */}
                {historyModal.data.opening && (
                  <div>
                    <h4 className="font-semibold mb-2 text-green-700">Opening Denominations</h4>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-green-50">
                            <TableHead>Denom</TableHead>
                            <TableHead>Count</TableHead>
                            <TableHead>Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(historyModal.data.opening.denominations || {})
                            .sort(([a], [b]) => parseInt(a) - parseInt(b))
                            .map(([denom, count]) => (
                              <TableRow key={denom}>
                                <TableCell className="font-medium">{denom}</TableCell>
                                <TableCell>{count}</TableCell>
                                <TableCell className="font-medium">{(parseInt(denom) * count).toLocaleString('en-IN')}</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Closing Denominations */}
                {historyModal.data.closing && (
                  <div>
                    <h4 className="font-semibold mb-2 text-blue-700">Closing Denominations</h4>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-blue-50">
                            <TableHead>Denom</TableHead>
                            <TableHead>Count</TableHead>
                            <TableHead>Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(historyModal.data.closing.denominations || {})
                            .sort(([a], [b]) => parseInt(a) - parseInt(b))
                            .map(([denom, count]) => (
                              <TableRow key={denom}>
                                <TableCell className="font-medium">{denom}</TableCell>
                                <TableCell>{count}</TableCell>
                                <TableCell className="font-medium">{(parseInt(denom) * count).toLocaleString('en-IN')}</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

     {/* Denominations Management Modal */}
<Dialog open={denominationsModal} onOpenChange={setDenominationsModal}>
  <DialogContent className="max-w-md bg-white border border-gray-200 shadow-xl rounded-xl p-0">
    <DialogHeader className="px-4 py-3 border-b bg-gray-50 rounded-t-xl">
      <DialogTitle className="text-base font-semibold text-gray-900">
        Coin Denominations Settings
      </DialogTitle>
    </DialogHeader>

    <div className="p-4 space-y-4">
      {/* Add Denomination */}
      <div className="flex gap-2">
        <Input
          type="number"
          placeholder="Enter value (e.g., 1, 2, 5)"
          value={newDenomValue}
          onChange={(e) => setNewDenomValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAddDenomination()}
          className="h-10 bg-white border-gray-300 text-sm"
        />
        <Button
          onClick={handleAddDenomination}
          data-testid="add-denomination-button"
          className="bg-blue-700 hover:bg-blue-800 text-white text-sm"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add
        </Button>
      </div>

      {/* Active Denominations */}
      <div>
        <h4 className="text-sm font-semibold text-gray-800 mb-2">
          Active Denominations
        </h4>

        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-100">
                <TableHead className="text-xs font-semibold text-gray-700">
                  SL
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-700">
                  VALUE ()
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-700 text-right">
                  ACTION
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {denominations.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="text-center text-gray-500 text-sm py-6"
                  >
                    No denominations found
                  </TableCell>
                </TableRow>
              ) : (
                denominations
                  .sort((a, b) => a.value - b.value)
                  .map((d, idx) => (
                    <TableRow key={d.value} className="hover:bg-gray-50">
                      <TableCell className="text-sm">{idx + 1}</TableCell>
                      <TableCell className="text-sm font-medium">
                        {d.value}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDeleteDenomination(d.value)}
                          data-testid={`delete-denomination-${d.value}`}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  </DialogContent>
</Dialog>


    {/* Delete Confirmation Modal */}
<Dialog
  open={deleteModal.open}
  onOpenChange={(open) =>
    !open && setDeleteModal({ open: false, entryId: null })
  }
>
  <DialogContent className="max-w-sm bg-white border border-gray-200 shadow-xl rounded-xl p-0">
    <DialogHeader className="px-4 py-3 border-b bg-gray-50 rounded-t-xl">
      <DialogTitle className="text-base font-semibold text-gray-900">
        Confirm Delete
      </DialogTitle>
    </DialogHeader>

    <div className="px-4 py-5">
      <p className="text-sm text-gray-700">
        Are you sure you want to delete this entry?
      </p>
      <p className="text-sm text-gray-500 mt-1">
        This action cannot be undone.
      </p>
    </div>

    <DialogFooter className="px-4 py-3 border-t bg-gray-50 flex gap-2 justify-end">
      <Button
        variant="outline"
        onClick={() =>
          setDeleteModal({ open: false, entryId: null })
        }
        className="text-sm"
      >
        Cancel
      </Button>

      <Button
  onClick={handleDeleteEntry}
  data-testid="confirm-delete-button"
  className="
    bg-red-600 text-white
    hover:bg-red-700
    focus:ring-2 focus:ring-red-300
    transition-all duration-200
    hover:shadow-md
    active:scale-95
  "
>
  Delete
</Button>

    </DialogFooter>
  </DialogContent>
</Dialog>

    </LayoutWrapper>
  );
};

export default Coins;